import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";
import {
  FileBarChart, Loader2, Download, FileSpreadsheet, FileText,
  TrendingUp, TrendingDown, Equal, RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatCurrency } from "../../lib/utils";

const PRESETS = [
  { id: "today", label: "Hoy" },
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
  { id: "month", label: "Mes" },
  { id: "custom", label: "Personalizado" },
];

const KIND_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "purchase", label: "Compras" },
  { value: "sale_theoretical", label: "Ventas teóricas" },
  { value: "waste", label: "Caducidad" },
  { value: "breakage", label: "Rotura" },
  { value: "comp", label: "Regalo/Cortesía" },
  { value: "staff_meal", label: "Staff meal" },
  { value: "count_adjust", label: "Ajuste de conteo" },
  { value: "transfer", label: "Traslado" },
  { value: "initial_balance", label: "Saldo inicial" },
];

const ZONE_OPTIONS = [
  { value: "all", label: "Todas" },
  { value: "barra", label: "Barra" },
  { value: "cocina", label: "Cocina" },
  { value: "almacen", label: "Almacén" },
  { value: "camara", label: "Cámara" },
];

function todayISO() { return new Date().toISOString().slice(0, 10); }
function daysAgoISO(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function firstOfMonthISO() {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
}

function kindBadge(kind) {
  const map = {
    purchase: "bg-emerald-100 text-emerald-700 border-emerald-200",
    initial_balance: "bg-emerald-100 text-emerald-700 border-emerald-200",
    sale_theoretical: "bg-blue-100 text-blue-700 border-blue-200",
    waste: "bg-amber-100 text-amber-700 border-amber-200",
    breakage: "bg-red-100 text-red-700 border-red-200",
    comp: "bg-purple-100 text-purple-700 border-purple-200",
    staff_meal: "bg-cyan-100 text-cyan-700 border-cyan-200",
    count_adjust: "bg-muted text-foreground border-border",
    transfer: "bg-orange-100 text-orange-700 border-orange-200",
    manual_adjust: "bg-muted text-foreground border-border",
  };
  const label = KIND_OPTIONS.find((k) => k.value === kind)?.label || kind;
  return <span className={`px-1.5 py-0.5 rounded text-[10px] border ${map[kind] || map.manual_adjust}`}>{label}</span>;
}

export function InventoryReports({ restaurantId }) {
  const [preset, setPreset] = useState("30d");
  const [dateFrom, setDateFrom] = useState(daysAgoISO(29));
  const [dateTo, setDateTo] = useState(todayISO());
  const [zone, setZone] = useState("all");
  const [kind, setKind] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (preset === "today") { setDateFrom(todayISO()); setDateTo(todayISO()); }
    else if (preset === "7d") { setDateFrom(daysAgoISO(6)); setDateTo(todayISO()); }
    else if (preset === "30d") { setDateFrom(daysAgoISO(29)); setDateTo(todayISO()); }
    else if (preset === "month") { setDateFrom(firstOfMonthISO()); setDateTo(todayISO()); }
  }, [preset]);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("from", dateFrom);
      if (dateTo) params.set("to", dateTo + "T23:59:59Z");
      if (zone !== "all") params.set("zone", zone);
      if (kind !== "all") params.set("kind", kind);
      const d = await api.request(`/restaurants/${restaurantId}/stock-movements/report?${params.toString()}`);
      setData(d);
    } catch (e) {
      toast.error(e.message || "Error al cargar el reporte");
    } finally {
      setLoading(false);
    }
  }, [restaurantId, dateFrom, dateTo, zone, kind]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (format) => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo + "T23:59:59Z");
    if (zone !== "all") params.set("zone", zone);
    if (kind !== "all") params.set("kind", kind);
    const url = `/restaurants/${restaurantId}/stock-movements/report/${format}?${params.toString()}`;
    const filename = `inventario-reporte-${dateFrom}_${dateTo}.${format}`;
    try {
      await api.downloadFile(url, filename);
      toast.success(`Reporte ${format.toUpperCase()} descargado`);
    } catch (e) {
      toast.error(e.message || "Error al exportar");
    }
  };

  const maxDailyValue = useMemo(() => {
    if (!data?.daily) return 1;
    return Math.max(1, ...data.daily.flatMap((d) => [d.entradas || 0, Math.abs(d.mermas || 0), Math.abs(d.ventas || 0), Math.abs(d.ajustes || 0)]));
  }, [data]);

  return (
    <div className="space-y-4" data-testid="inventory-reports-view">
      {/* Header + filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <FileBarChart className="w-5 h-5 text-primary" />
              <h2 className="font-semibold">Reporte de movimientos</h2>
            </div>
            <div className="flex items-center gap-1.5" data-testid="reports-presets">
              {PRESETS.map((p) => (
                <Button
                  key={p.id} size="sm" variant={preset === p.id ? "default" : "outline"}
                  onClick={() => setPreset(p.id)} className="h-7 px-2.5 text-xs"
                  data-testid={`reports-preset-${p.id}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
            <div>
              <Label className="text-[11px] text-muted-foreground">Desde</Label>
              <Input
                type="date" value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPreset("custom"); }}
                className="h-9 text-sm"
                data-testid="reports-date-from"
              />
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Hasta</Label>
              <Input
                type="date" value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPreset("custom"); }}
                className="h-9 text-sm"
                data-testid="reports-date-to"
              />
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Zona</Label>
              <Select value={zone} onValueChange={setZone}>
                <SelectTrigger className="h-9 text-sm" data-testid="reports-zone-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ZONE_OPTIONS.map((z) => <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Tipo</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="h-9 text-sm" data-testid="reports-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KIND_OPTIONS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2 justify-end flex-wrap">
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reports-refresh-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5 mr-1" />}
              Actualizar
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDownload("csv")} data-testid="reports-export-csv">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDownload("pdf")} data-testid="reports-export-pdf">
              <FileText className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading && !data ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : data ? (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryCard label="Entradas" value={data.summary.entradas_value} prev={data.previous_summary?.entradas_value} count={data.summary.entradas_count} icon={TrendingUp} color="emerald" />
            <SummaryCard label="Ventas teóricas" value={data.summary.ventas_value} prev={data.previous_summary?.ventas_value} count={data.summary.ventas_count} icon={TrendingDown} color="blue" />
            <SummaryCard label="Mermas" value={data.summary.mermas_value} prev={data.previous_summary?.mermas_value} count={data.summary.mermas_count} icon={TrendingDown} color="red" invertDelta />
            <SummaryCard label="Ajustes" value={data.summary.ajustes_value} prev={data.previous_summary?.ajustes_value} count={data.summary.ajustes_count} icon={Equal} color="amber" />
          </div>

          {/* Daily bar chart */}
          {data.daily && data.daily.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <div className="text-sm font-medium mb-2">Actividad diaria</div>
                <div className="flex items-end gap-1 h-32 overflow-x-auto" data-testid="reports-daily-chart">
                  {data.daily.map((d) => {
                    const h = (v) => `${Math.max(2, Math.min(100, (Math.abs(v) / maxDailyValue) * 100))}%`;
                    return (
                      <div key={d.date} className="flex flex-col items-center gap-0.5 min-w-[18px]">
                        <div className="flex-1 flex flex-col-reverse w-full gap-[1px]">
                          {d.entradas > 0 && <div title={`Entradas: €${d.entradas.toFixed(2)}`} className="bg-emerald-500 rounded-t" style={{ height: h(d.entradas) }} />}
                          {d.mermas < 0 && <div title={`Mermas: €${d.mermas.toFixed(2)}`} className="bg-red-500" style={{ height: h(d.mermas) }} />}
                          {d.ventas < 0 && <div title={`Ventas: €${d.ventas.toFixed(2)}`} className="bg-blue-400" style={{ height: h(d.ventas) }} />}
                          {d.ajustes !== 0 && <div title={`Ajustes: €${d.ajustes.toFixed(2)}`} className="bg-amber-500" style={{ height: h(d.ajustes) }} />}
                        </div>
                        <span className="text-[8px] text-muted-foreground">{d.date.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-3 mt-2 text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm" /> Entradas</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-sm" /> Mermas</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-400 rounded-sm" /> Ventas</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 rounded-sm" /> Ajustes</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Movements table */}
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between p-3 border-b">
                <span className="text-sm font-medium">Movimientos ({data.movements.length})</span>
                {data.movements.length >= 2000 && (
                  <Badge variant="secondary" className="text-[10px]">Mostrando primeros 2000. Afina el filtro o exporta.</Badge>
                )}
              </div>
              {data.movements.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-10">
                  No hay movimientos con los filtros actuales.
                </p>
              ) : (
                <div className="max-h-[500px] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background z-10">
                      <TableRow>
                        <TableHead className="text-xs">Fecha</TableHead>
                        <TableHead className="text-xs">Producto</TableHead>
                        <TableHead className="text-xs">Tipo</TableHead>
                        <TableHead className="text-xs text-right">Cantidad</TableHead>
                        <TableHead className="text-xs text-right">Valor</TableHead>
                        <TableHead className="text-xs">Zona</TableHead>
                        <TableHead className="text-xs">Nota</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.movements.slice(0, 2000).map((m) => {
                        const qty = Number(m.qty || 0);
                        const cost = Number(m.cost_at_moment || 0);
                        const value = qty * cost;
                        return (
                          <TableRow key={m.movement_id} data-testid={`mov-row-${m.movement_id}`}>
                            <TableCell className="text-xs whitespace-nowrap">{(m.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                            <TableCell className="text-xs truncate max-w-[200px]">{m.ingredient_name}</TableCell>
                            <TableCell>{kindBadge(m.kind)}</TableCell>
                            <TableCell className={`text-xs text-right font-mono tabular-nums ${qty < 0 ? "text-red-700" : "text-emerald-700"}`}>
                              {qty > 0 ? "+" : ""}{qty.toFixed(2)} {m.unit}
                            </TableCell>
                            <TableCell className={`text-xs text-right font-mono tabular-nums ${value < 0 ? "text-red-700" : "text-emerald-700"}`}>
                              {value >= 0 ? "+" : ""}{formatCurrency(value)}
                            </TableCell>
                            <TableCell className="text-xs">{m.zone || "—"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground truncate max-w-[200px]">{m.note || "—"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}

function SummaryCard({ label, value, prev, count, icon: Icon, color, invertDelta = false }) {
  const colorMap = {
    emerald: "border-emerald-200 bg-emerald-50/50 text-emerald-700",
    blue: "border-blue-200 bg-blue-50/50 text-blue-700",
    red: "border-red-200 bg-red-50/50 text-red-700",
    amber: "border-amber-200 bg-amber-50/50 text-amber-700",
  };

  // Delta calculation — compares absolute change vs previous period.
  // `invertDelta` swaps the green/red semantics for metrics where "less is better"
  // (e.g. losses: -10% vs previous period should show green).
  let deltaNode = null;
  if (prev !== undefined && prev !== null) {
    const absDelta = Math.abs(value) - Math.abs(prev);
    const basis = Math.abs(prev);
    const pct = basis > 0 ? (absDelta / basis) * 100 : (Math.abs(value) > 0 ? 100 : 0);
    let goodNews = absDelta >= 0;
    if (invertDelta) goodNews = !goodNews;
    const arrow = absDelta > 0 ? "▲" : absDelta < 0 ? "▼" : "=";
    const cls = absDelta === 0
      ? "text-muted-foreground"
      : goodNews ? "text-emerald-700" : "text-red-700";
    const label2 = basis === 0 && Math.abs(value) > 0 ? "nuevo" : `${arrow} ${Math.abs(pct).toFixed(0)}%`;
    deltaNode = (
      <span className={`text-[10px] font-medium tabular-nums ml-1.5 ${cls}`} title={`Periodo anterior: ${prev >= 0 ? "+" : ""}${prev.toFixed(2)} €`}>
        {label2}
      </span>
    );
  }

  return (
    <Card className={`border ${colorMap[color]} shadow-sm`} data-testid={`reports-summary-${color}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider opacity-80">
          <Icon className="w-3.5 h-3.5" /> {label}
        </div>
        <p className="text-xl font-bold mt-1.5 text-foreground tabular-nums">
          {value >= 0 ? "+" : ""}{formatCurrency(value)}
          {deltaNode}
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5">{count} movimiento(s)</p>
      </CardContent>
    </Card>
  );
}

export default InventoryReports;
